/**
 * RemoveTagsDialog - Dialog para remover tags dos artigos selecionados
 *
 * Mostra as tags presentes nos artigos selecionados e permite escolher quais remover
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  useTheme
} from '@mui/material';
import {
  LocalOfferOutlined as TagIcon,
  RemoveCircleOutline as RemoveIcon,
  CheckCircle as SuccessIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

const RemoveTagsDialog = ({
  open,
  onClose,
  articleIds = [],
  onSuccess
}) => {
  const theme = useTheme();
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [totalArticles, setTotalArticles] = useState(0);

  useEffect(() => {
    if (open && articleIds.length > 0) {
      fetchCommonTags();
      setSelectedTags([]);
      setError(null);
      setSuccess(null);
    }
  }, [open, articleIds]);

  const fetchCommonTags = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.post('/articles/common-tags', {
        articleIds
      });

      if (res.data.success) {
        setAllTags(res.data.allTags || []);
        setTotalArticles(res.data.totalArticles || articleIds.length);

        if (res.data.allTags.length === 0) {
          setError('Nenhuma tag encontrada nos artigos selecionados');
        }
      } else {
        setError(res.data.error || 'Erro ao buscar tags');
      }
    } catch (err) {
      console.error('Erro ao buscar tags dos artigos:', err);
      setError(err.response?.data?.error || 'Erro ao buscar tags dos artigos');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTag = (tagId) => {
    setSelectedTags(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      }
      return [...prev, tagId];
    });
  };

  const handleSelectAll = () => {
    if (selectedTags.length === allTags.length) {
      setSelectedTags([]);
    } else {
      setSelectedTags(allTags.map(t => t._id));
    }
  };

  const handleRemoveTags = async () => {
    if (selectedTags.length === 0) return;

    try {
      setSaving(true);
      setError(null);

      const res = await api.post('/articles/bulk-remove-tags', {
        articleIds,
        tagIds: selectedTags
      });

      if (res.data.success) {
        setSuccess(`Tags removidas de ${res.data.modifiedCount} artigos`);
        setTimeout(() => {
          onSuccess?.(res.data.modifiedCount);
          onClose();
        }, 1500);
      } else {
        setError(res.data.error || 'Erro ao remover tags');
      }
    } catch (err) {
      console.error('Erro ao remover tags:', err);
      setError(err.response?.data?.error || 'Erro ao remover tags');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <PlatformDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <PlatformDialogTitle
        title="Remover Tags"
        subtitle={`${articleIds.length} artigo(s) selecionado(s)`}
        icon={<RemoveIcon color="error" />}
        onClose={handleClose}
        color="error.main"
      />

      <DialogContent sx={{ pt: 3.5 }}>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" mt={2}>
              Buscando tags dos artigos...
            </Typography>
          </Box>
        ) : error && allTags.length === 0 ? (
          <Alert severity="info" sx={{ my: 2 }}>
            {error}
          </Alert>
        ) : success ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <SuccessIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" color="success.main">
              {success}
            </Typography>
          </Box>
        ) : (
          <>
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Selecione as tags que deseja remover dos artigos selecionados.
              </Typography>
            </Box>

            {allTags.length > 0 && (
              <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  startIcon={<Checkbox
                    checked={selectedTags.length === allTags.length}
                    indeterminate={selectedTags.length > 0 && selectedTags.length < allTags.length}
                    size="small"
                  />}
                >
                  {selectedTags.length === allTags.length ? 'Desmarcar todas' : 'Selecionar todas'}
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {selectedTags.length} selecionadas
                </Typography>
              </Box>
            )}

            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {allTags.map((tag) => (
                <ListItem
                  key={tag._id}
                  disablePadding
                  sx={{
                    mb: 0.5,
                    borderRadius: 1,
                    bgcolor: selectedTags.includes(tag._id)
                      ? `${theme.palette.error.main}10`
                      : 'transparent',
                    '&:hover': {
                      bgcolor: `${theme.palette.error.main}08`
                    }
                  }}
                >
                  <ListItemButton onClick={() => handleToggleTag(tag._id)} dense>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={selectedTags.includes(tag._id)}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                        color="error"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <TagIcon fontSize="small" sx={{ color: tag.color || 'primary.main' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {tag.name}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Em {tag.count} de {totalArticles} artigo(s)
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
        <Button onClick={handleClose} disabled={saving}>
          {success ? 'Fechar' : 'Cancelar'}
        </Button>
        {!success && allTags.length > 0 && (
          <Button
            variant="contained"
            color="error"
            onClick={handleRemoveTags}
            disabled={saving || selectedTags.length === 0}
            startIcon={saving ? <CircularProgress size={16} /> : <RemoveIcon />}
          >
            {saving ? 'Removendo...' : `Remover (${selectedTags.length})`}
          </Button>
        )}
      </DialogActions>

      {saving && <LinearProgress color="error" />}
    </PlatformDialog>
  );
};

export default RemoveTagsDialog;

